/**
 * Capital Readiness Score Dashboard - Phase 8.
 * KPI cards, leaderboard table, filters.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  Gauge,
  AlertTriangle,
  Users,
  Search,
  PlayCircle,
  History as HistoryIcon,
  Target,
  Wrench,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import capitalReadinessService from '../services/capitalReadinessService';
import {
  CapitalReadinessScore,
  CRSLeaderboardEntry,
  CRSTier,
} from '../types/capitalReadiness';
import CRSTierBadge from '../components/CRSTierBadge';
import CRSRadarChart from '../components/CRSRadarChart';

const TIER_OPTIONS: Array<{ value: '' | CRSTier; label: string }> = [
  { value: '', label: 'All tiers' },
  { value: 'investment_ready', label: 'Investment Ready' },
  { value: 'near_ready', label: 'Near Ready' },
  { value: 'developing', label: 'Developing' },
  { value: 'early_stage', label: 'Early Stage' },
  { value: 'not_ready', label: 'Not Ready' },
];

export function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 65) return 'text-emerald-600';
  if (score >= 50) return 'text-amber-600';
  if (score >= 35) return 'text-orange-600';
  return 'text-red-600';
}

export function scoreBgColor(score: number): string {
  if (score >= 80) return 'bg-green-100 text-green-700';
  if (score >= 65) return 'bg-emerald-100 text-emerald-700';
  if (score >= 50) return 'bg-amber-100 text-amber-700';
  if (score >= 35) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
}

const CRSDashboardPage: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<CRSLeaderboardEntry[]>([]);
  const [scores, setScores] = useState<CapitalReadinessScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState<'' | CRSTier>('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    const [lbRes, scoresRes] = await Promise.all([
      capitalReadinessService.fetchLeaderboard(20),
      capitalReadinessService.fetchScores({ size: 200 }),
    ]);
    if (lbRes.error) {
      toast.error(lbRes.error);
    } else {
      setLeaderboard(lbRes.data || []);
    }
    if (scoresRes.error) {
      toast.error(scoresRes.error);
    } else {
      setScores(scoresRes.data?.scores || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    scores.forEach((s) => {
      counts[s.tier] = (counts[s.tier] || 0) + 1;
    });
    return counts;
  }, [scores]);

  const medianScore = useMemo(() => {
    if (scores.length === 0) return 0;
    const sorted = scores.map((s) => s.overall_score).sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  }, [scores]);

  const criticalGapCount = useMemo(() => {
    return scores.filter((s) =>
      (s.gaps || []).some((g) => g.severity === 'critical')
    ).length;
  }, [scores]);

  const filtered = useMemo(() => {
    return leaderboard.filter((e) => {
      if (tierFilter && e.tier !== tierFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(e.entity_name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [leaderboard, tierFilter, search]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
              <Gauge className="w-6 h-6 mr-2 text-blue-600" />
              Capital Readiness
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Score founders across 7 dimensions. Surface gaps, recommend
              providers, track improvement over time.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/capital-readiness/score"
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Score an entity
            </Link>
            <Link
              to="/capital-readiness/history"
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <HistoryIcon className="w-4 h-4 mr-2" />
              History
            </Link>
            <Link
              to="/capital-readiness/gaps"
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <Target className="w-4 h-4 mr-2" />
              Gap analysis
            </Link>
            <Link
              to="/capital-readiness/providers"
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Providers
            </Link>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            label="Investment Ready"
            value={tierCounts.investment_ready || 0}
            color="text-green-600"
          />
          <KpiCard
            label="Near Ready"
            value={tierCounts.near_ready || 0}
            color="text-emerald-600"
          />
          <KpiCard
            label="Developing"
            value={tierCounts.developing || 0}
            color="text-amber-600"
          />
          <KpiCard
            label="Early / Not Ready"
            value={(tierCounts.early_stage || 0) + (tierCounts.not_ready || 0)}
            color="text-red-600"
          />
          <KpiCard
            label="Median score"
            value={Math.round(medianScore)}
            color="text-blue-600"
          />
          <KpiCard
            label="Entities with critical gaps"
            value={criticalGapCount}
            color="text-red-600"
            icon={<AlertTriangle className="w-4 h-4 text-red-500" />}
          />
          <KpiCard
            label="Total scored"
            value={scores.length}
            color="text-gray-700"
            icon={<Users className="w-4 h-4 text-gray-500" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search entities..."
              className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {TIER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Leaderboard */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Leaderboard — top 20
            </h3>
            <span className="text-xs text-gray-500">
              {filtered.length} entr{filtered.length === 1 ? 'y' : 'ies'}
            </span>
          </div>
          {/* Top-3 radar carousel — visual readout of the dimension breakdown.
              Joins leaderboard (filterable display) with `scores` (full CRS rows
              that have dimension_scores) by startup_id/project_id. */}
          {!loading && filtered.length > 0 && (() => {
            const lookup = new Map<string, CapitalReadinessScore>();
            for (const s of scores) {
              const key = s.startup_id ? `s:${s.startup_id}` : `p:${s.project_id}`;
              if (!lookup.has(key)) lookup.set(key, s);
            }
            const top3 = filtered.slice(0, 3).map((e) => {
              const key = e.entity_kind === 'startup' ? `s:${e.startup_id}` : `p:${e.project_id}`;
              return { e, full: lookup.get(key) };
            });
            return (
              <div className="border-b border-gray-200 px-4 py-4 bg-gray-50">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  Top 3 by Capital Readiness
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {top3.map(({ e, full }, i) => (
                    <Link
                      key={`top-${e.id}`}
                      to={full ? `/capital-readiness/scores/${full.id}` : '/capital-readiness'}
                      className="block bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-400 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs uppercase tracking-wide text-gray-500">
                            #{i + 1}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {e.entity_name}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold leading-none ${scoreColor(e.overall_score)}`}>
                            {e.overall_score}
                          </div>
                          <CRSTierBadge tier={e.tier} size="sm" />
                        </div>
                      </div>
                      {full?.dimension_scores ? (
                        <CRSRadarChart scores={full.dimension_scores as any} size={200} />
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-xs text-gray-400">
                          (radar unavailable — view detail)
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}

          {loading ? (
            <div className="p-10 flex items-center justify-center text-gray-600">
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">
              No scored entities yet.{' '}
              <Link to="/capital-readiness/score" className="text-blue-600 hover:underline">
                Score one now
              </Link>
              .
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 w-12">
                    #
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Entity
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Score
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Tier
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Gaps
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((e, idx) => (
                  <tr key={e.id}>
                    <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">
                        {e.entity_name || `${e.entity_kind} #${e.startup_id || e.project_id}`}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {e.entity_kind}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={`text-lg font-bold ${scoreColor(e.overall_score)}`}
                      >
                        {Math.round(e.overall_score)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <CRSTierBadge tier={e.tier} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {e.gap_count}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/capital-readiness/scores/${e.id}`}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: number | string;
  color?: string;
  icon?: React.ReactNode;
}> = ({ label, value, color = 'text-gray-700', icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wide text-gray-500">
        {label}
      </div>
      {icon}
    </div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </div>
);

export default CRSDashboardPage;
